import constants from '../../settings/constants';
import { applicationSettings } from '../../settings/application';
import {
  actionSetAddToCartState,
  actionSetAddToListState,
  actionSetAddUnavailableProductsToList,
  actionSetConfirmationCardType,
  actionSetShareDesignCardState,
  actionSetHideModal,
  actionSetModalType,
  actionSetShoppingItems,
  actionSetShowModal,
  actionSetStoreAvailabilities,
  actionSetStoreId,
  actionSetDesignLink,
  actionSetFailedShoppingItems,
  actionSetSendByEmailState,
  actionSetHomeDeliveryState,
  actionSetHomeDeliveryZipCode,
  actionSetHomeDeliveryZipAvailabilities,
  actionSetFinancingOptions,
} from './summaryActions';
import { actionSetOverlayState } from '../popups/popupsActions';
import {
  selectHomeDelivery,
  selectShoppingItems,
  selectStoreId,
} from './summarySelectors';
import { selectTac } from '../tac/tacSelectors';
import { selectDexfSettings } from '../dexfSettings/dexfSettingsSelectors';
import { NotificationLinkTypeEnum } from '@inter-ikea-kompis/enums';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';
import { getShoppingItemsFromTAC } from '../../util/aactools/kompisConvert';
import {
  SummaryPageModalEnum,
  SummaryPageConfirmationCardTypeEnum,
} from '@inter-ikea-kompis/component-summary-page';
import ZipInCardStateEnum from '@inter-ikea-kompis/component-zip-in/lib/enums/ZipInCardStateEnum.js';
import { AddToBagStateEnum } from '@inter-ikea-kompis/component-add-to-bag';
import wait from '../../util/wait';
import {
  thunkResetSavedVpc,
  thunkSaveVpc,
  thunkRetrySaveVpcIfNeeded,
} from '../vpc/vpcThunks';
import { thunkFetchStores } from '../stores/storeThunk';
import {
  selectDirtyConfigurationState,
  selectSavedVpcCode,
} from '../vpc/vpcSelectors';
import { SummaryModal, SummaryCards } from './summaryTypes';
import { Thunk } from '../../generalTypes';
import {
  ICart,
  IFailedShoppingItem,
  IShoppingItem,
  IShoppingProduct,
  IZipAvailability,
} from '@inter-ikea-kompis/types';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import { renderToast } from '../../services/toastMaster';
import {
  getNotificationService,
  getZipValidationService,
  getShoppingCartService,
  getShoppingListService,
  getCheckoutAvailabilityService,
  getFinancingService,
} from '../../services/ServiceHandler';

const {
  INITIAL_VIEW,
  VIEW_NAMES: { SUMMARY },
} = constants;

export const confirmationModal: SummaryModal = {
  itemId: null,
  modal: SummaryPageModalEnum.confirmationCard,
};

export const shareDesignModal: SummaryModal = {
  itemId: null,
  modal: SummaryPageModalEnum.shareDesignCard,
};

export const buyInStore: SummaryModal = {
  itemId: null,
  modal: SummaryPageModalEnum.buyInStore,
};

export const homeDeliveryModal: SummaryModal = {
  itemId: null,
  modal: SummaryPageModalEnum.zipIn,
};

export const unavailableProductModal: SummaryModal = {
  itemId: null,
  modal: SummaryPageModalEnum.unavailableProductCard,
};

interface IAddToCartError {
  failedShoppingItems?: IFailedShoppingItem[];
}

/**
 * Loads necessary data on for summary page
 * @param sceneImage
 */
export const thunkInitSummary: Thunk = () => async (dispatch, getState) => {
  dispatch(thunkFetchStores());
  dispatch(thunkGetShoppingItems());

  const state = getState();
  const dirtyConfiguration = selectDirtyConfigurationState(state);
  if (dirtyConfiguration) {
    dispatch(thunkResetSavedVpcAndDesignLink());
    dispatch(thunkSaveVpcAndGenerateDesignLink());
  }

  // If storeId sent through URL, set availability
  if (applicationSettings.storeId) {
    dispatch(thunkSetStoreAvailabilities());
  }
};

/**
 * Reset saved VPC and design link.
 */
export const thunkResetSavedVpcAndDesignLink: Thunk =
  () => (dispatch, getState) => {
    dispatch(thunkResetSavedVpc());
    dispatch(actionSetDesignLink(''));
  };

/**
 * First save VPC, and after that generate design link.
 */
export const thunkSaveVpcAndGenerateDesignLink: Thunk =
  () => async (dispatch, getState) => {
    await dispatch(thunkSaveVpc());
    await dispatch(thunkGenerateDesignLink());
  };

export const thunkSetSummaryInstanceState: Thunk<
  [SummaryModal, SummaryCards]
> = (modal, cards) => dispatch => {
  cards.confirmationCard &&
    dispatch(actionSetConfirmationCardType(cards.confirmationCard));
  cards.shareDesignCard &&
    dispatch(actionSetShareDesignCardState(cards.shareDesignCard));
  dispatch(actionSetModalType(modal));
  dispatch(actionSetShowModal());
};

/**
 * Get shopping items
 */
export const thunkGetShoppingItems: Thunk = () => (dispatch, getState) => {
  const state = getState();
  const tac = selectTac(state);

  if (!tac) return;

  dispatch(actionSetShoppingItems(getShoppingItemsFromTAC(tac, true)));
};

/**
 * Set store availabilities
 */
export const thunkSetStoreAvailabilities: Thunk =
  () => async (dispatch, getState) => {
    const state = getState();
    const storeId = selectStoreId(state);
    const tac = selectTac(state);
    const shoppingItems = selectShoppingItems(state);

    if (!tac || !storeId) return dispatch(actionSetStoreAvailabilities([]));

    try {
      const specificStoreAvailabilities =
        await getCheckoutAvailabilityService().getStoreAvailabilities(
          storeId,
          shoppingItems
        );

      dispatch(actionSetStoreAvailabilities(specificStoreAvailabilities));
    } catch (error) {
      console.warn(
        'CheckoutAvailabilityService failed to fetch availabilities'
      );
      console.error(error);
    }
  };

/**
 *
 * @param email
 * @param vpcCode
 * @returns
 */
export const thunkOnSendByEmailSend: Thunk<[string, string]> =
  (email, vpcCode) => (dispatch, getState) => {
    dispatch(actionSetSendByEmailState(SendByEmailStateEnum.loading));

    try {
      getNotificationService()
        .sendEmail({
          vpcCode,
          email,
          linkType: NotificationLinkTypeEnum.pickinglist,
        })
        .then(() =>
          dispatch(actionSetSendByEmailState(SendByEmailStateEnum.success))
        )
        .catch(() =>
          dispatch(actionSetSendByEmailState(SendByEmailStateEnum.failed))
        );
    } catch (error) {
      console.error(error);
      dispatch(actionSetSendByEmailState(SendByEmailStateEnum.failed));
    }
  };

/**
 * Handle home delivery
 */
export const thunkHandleHomeDelivery =
  (zipCode?: any) => async (dispatch: any, getState: any) => {
    const homeDeliveryInputValue = zipCode
      ? zipCode
      : selectHomeDelivery(getState()).zipCode;
    const shoppingItems = selectShoppingItems(getState());
    dispatch(actionSetHomeDeliveryState(ZipInCardStateEnum.loading));

    // Using the Zip Validation Service to first check if the given zip code is valid or
    // not, before retrieving information about it with the Checkout Availability Service
    const validateZipCode = async () => {
      try {
        if (homeDeliveryInputValue) {
          const validate = await getZipValidationService().getZipValidation(
            homeDeliveryInputValue
          );
          return validate;
        }
      } catch (error) {
        dispatch(actionSetHomeDeliveryState(ZipInCardStateEnum.noZipInfo));
        console.error(error);
        return false;
      }
    };

    const zipValidation = await validateZipCode();
    if (!zipValidation) return;

    let zipAvailabilities: IZipAvailability[] = [];
    if (zipValidation.valid && zipValidation.content) {
      try {
        zipAvailabilities =
          await getCheckoutAvailabilityService().getZipAvailabilities(
            // Send in the formatted zip code
            zipValidation.content.formatted,
            shoppingItems
          );
      } catch (error) {
        console.error(error);
      }

      if (!zipAvailabilities || zipAvailabilities.length === 0) {
        dispatch(actionSetHomeDeliveryState(ZipInCardStateEnum.noZipInfo));
        dispatch(actionSetHomeDeliveryZipCode(homeDeliveryInputValue));
      } else {
        dispatch(actionSetHomeDeliveryState(ZipInCardStateEnum.submitted));
        dispatch(actionSetHomeDeliveryZipCode(homeDeliveryInputValue));
        dispatch(actionSetHomeDeliveryZipAvailabilities(zipAvailabilities));
        dispatch(actionSetOverlayState(false));
        dispatch(actionSetHideModal());
      }
    } else {
      dispatch(actionSetHomeDeliveryState(ZipInCardStateEnum.invalidInput));
    }
  };

/**
 * Handle home delivery
 */
export const thunkHandleFinancingOptions =
  (shoppingProducts: IShoppingProduct[]) =>
  async (dispatch: any, getState: any) => {
    getFinancingService()
      .getFinancingOption(shoppingProducts)
      .then((financingOption: any) => {
        dispatch(actionSetFinancingOptions(financingOption));
      })
      .catch((error: Error) => {
        // Hide the financial services component in case of error.
        dispatch(actionSetFinancingOptions(undefined));
        console.log(error);
      });
  };

/**
 * Set selected store
 * @param storeId
 */
export const thunkSetSelectedStore: Thunk<[string]> =
  storeId => async dispatch => {
    dispatch(actionSetStoreId(storeId));
    dispatch(thunkSetStoreAvailabilities());
  };

export const thunkGenerateDesignLink =
  () => async (dispatch: any, getState: any) => {
    const vpc = selectSavedVpcCode(getState());
    const splitLocale = applicationSettings.locale.split('-');
    const localeLanguageCode = splitLocale[0];
    const countryCode = splitLocale[1];
    const locale = `?locale=${localeLanguageCode}_${countryCode}`;

    const storeId = selectStoreId(getState());
    const storeIdQueryParam = storeId ? `&storeId=${storeId}` : '';
    const applicationName = applicationSettings.applicationName.toLowerCase();
    const initialView = `&${INITIAL_VIEW}=${SUMMARY}`;
    const hostPath = window.location.host + window.location.pathname;
    const trimmedPath = hostPath.replace('index.html', '');
    let url = '';

    if (vpc) {
      if (
        //dev environment
        trimmedPath.includes('localhost') ||
        trimmedPath.includes('azurewebsites.')
      ) {
        url = `${trimmedPath}m2/#/${applicationName}/#${vpc}${locale}${storeIdQueryParam}${initialView}`;
      } else {
        try {
          //CTE & Prod
          const dexfSettings = selectDexfSettings(getState());
          url = dexfSettings.vpc.pickinglist.replace(
            /{CODE}/,
            `${vpc}${storeIdQueryParam}${initialView}`
          );
        } catch (error) {
          // Handle error.
          console.log(error);
        }
      }
    }

    dispatch(actionSetDesignLink(url));
  };

export const thunkOnAddUnavailableToCartAndList: Thunk<
  [
    {
      shoppingListItems: IShoppingItem[];
      shoppingCartItems: IShoppingItem[];
    }
  ]
> =
  ({ shoppingListItems, shoppingCartItems }) =>
  async (dispatch, getState) => {
    const WAITING_TIME_BETWEEN_LIST_AND_CART = 1000;

    const thereAreCartItems = !!shoppingCartItems.length;
    const thereAreListItems = !!shoppingListItems.length;

    dispatch(actionSetAddToCartState(AddToBagStateEnum.loading));

    await dispatch(thunkRetrySaveVpcIfNeeded());

    if (thereAreListItems) {
      dispatch(thunkOnAddToList(shoppingListItems, true));
      dispatch(actionSetFailedShoppingItems(shoppingListItems));
    }

    if (thereAreListItems && thereAreCartItems) {
      await wait(WAITING_TIME_BETWEEN_LIST_AND_CART);
      dispatch(actionSetHideModal());
    }

    if (thereAreCartItems)
      dispatch(
        thunkOnAddToCart({ shoppingCartItems: shoppingCartItems }, true)
      );
    else dispatch(actionSetAddToCartState(AddToBagStateEnum.default));
  };

export const thunkOnAddToList: Thunk<[boolean | IShoppingItem[], boolean]> =
  (items = false, fromAddUnavailable) =>
  async (dispatch, getState) => {
    const { addToListSuccess } = SummaryPageConfirmationCardTypeEnum;

    if (!fromAddUnavailable) {
      dispatch(actionSetAddToListState(AddToBagStateEnum.loading));
      await dispatch(thunkRetrySaveVpcIfNeeded());
    }

    const shoppingItems = items ? items : selectShoppingItems(getState());

    /**
     * Set add to list state failed
     */
    const setAddToListStateFailed = () => {
      !fromAddUnavailable &&
        dispatch(actionSetAddToListState(AddToBagStateEnum.default));
      renderToast('list', false, false);
    };

    /**
     * Set add to list state success
     */
    const setAddToListStateSuccess = async () => {
      dispatch(actionSetOverlayState(true));
      if (!fromAddUnavailable) {
        dispatch(actionSetAddToListState(AddToBagStateEnum.confirmation));
        dispatch(
          thunkSetSummaryInstanceState(confirmationModal, {
            confirmationCard: addToListSuccess,
          })
        );
      }
    };

    /**
     * Attempt to contact shopping list service
     */
    const tryShoppingListService = async () => {
      try {
        const vpcCode = selectSavedVpcCode(getState());
        return await getShoppingListService().addToList({
          items: shoppingItems,
          designCode: !fromAddUnavailable ? vpcCode : undefined,
        });
      } catch (error) {
        setAddToListStateFailed();
        return { errorList: [error] };
      }
    };

    /**
     * Handle shopping list errors
     */
    const handleShoppingListErrors = () => {
      setAddToListStateFailed();
      localStatisticsReporter.reportAddToListError();
    };

    !fromAddUnavailable && actionSetAddToListState(AddToBagStateEnum.loading);
    fromAddUnavailable && dispatch(actionSetAddUnavailableProductsToList(true));
    // onAddUnavailableItems gives us a different variable so we need to check both.
    const { errorList } = await tryShoppingListService();

    errorList.length ? handleShoppingListErrors() : setAddToListStateSuccess();
  };

/**
 * On add to cart
 *
 * @param items
 */
export const thunkOnAddToCart: Thunk<
  [
    {
      shoppingItems?: IShoppingItem[];
      shoppingCartItems: IShoppingItem[];
      completeConfiguration?: boolean;
    },
    boolean
  ]
> =
  (
    { shoppingItems, shoppingCartItems, completeConfiguration },
    fromAddUnavailable
  ) =>
  async (dispatch, getState) => {
    const shoppingCartProducts = shoppingItems || shoppingCartItems;
    const { addToCartSuccess } = SummaryPageConfirmationCardTypeEnum;

    /**
     * On add to cart error
     */
    const thunkOnAddToCartError: Thunk = () => dispatch => {
      dispatch(actionSetAddToCartState(AddToBagStateEnum.default));
      localStatisticsReporter.reportAddToBagError(shoppingCartProducts);
    };

    /**
     * Check if errors exist
     * @param errorList
     */
    const hasErrors = ({ errorList }: ICart) => {
      if (errorList.length) {
        dispatch(thunkOnAddToCartError());
        setAddToCartStateFailed();

        return true;
      }
    };

    /**
     * Set add to cart state successful
     */
    const setAddToCartStateSuccessful = () => {
      dispatch(actionSetOverlayState(true));
      dispatch(actionSetAddToCartState(AddToBagStateEnum.confirmation));
      dispatch(
        thunkSetSummaryInstanceState(confirmationModal, {
          confirmationCard: addToCartSuccess,
        })
      );
    };

    /**
     * Set add to cart state failed
     */
    const setAddToCartStateFailed = () => {
      dispatch(actionSetAddToCartState(AddToBagStateEnum.default));
      renderToast('cart', false, false);
    };

    /**
     * Attempt to add to cart
     */
    const tryShoppingCartService = async () => {
      try {
        const vpcCode = selectSavedVpcCode(getState());
        const cart = await getShoppingCartService().addToCart({
          items: shoppingCartProducts,
          designCode: vpcCode,
          addDesignCodeAsGroup: !!completeConfiguration && !!vpcCode,
        });

        if (!hasErrors(cart)) setAddToCartStateSuccessful();
      } catch (error) {
        const failedShoppingItems =
          (error as IAddToCartError).failedShoppingItems || [];
        if (failedShoppingItems.length > 0) {
          // Handling of unavailable products in NIF markets.
          dispatch(actionSetFailedShoppingItems(failedShoppingItems));
          dispatch(thunkSetSummaryInstanceState(unavailableProductModal, {}));
          dispatch(actionSetAddToCartState(AddToBagStateEnum.default));
        } else {
          dispatch(thunkOnAddToCartError());
          setAddToCartStateFailed();
        }
      }
    };

    if (!fromAddUnavailable) {
      dispatch(actionSetAddToCartState(AddToBagStateEnum.loading));
      await dispatch(thunkRetrySaveVpcIfNeeded());
    }

    await tryShoppingCartService();
  };

/**
 *
 */
export const thunkOnKioskMainCTA =
  () => async (dispatch: any, getState: any) => {
    const { saveVpcFail: saveVpcFailCard } =
      SummaryPageConfirmationCardTypeEnum;

    const onFailedVpcCode = () => {
      dispatch(actionSetOverlayState(true));
      dispatch(
        thunkSetSummaryInstanceState(confirmationModal, {
          confirmationCard: saveVpcFailCard,
        })
      );
    };

    const onSavedVpcCode = () => {
      dispatch(
        thunkSetSummaryInstanceState(buyInStore, {
          buyInStore: buyInStore.modal,
        })
      );
      dispatch(actionSetOverlayState(true));
    };

    if (selectSavedVpcCode(getState())) {
      onSavedVpcCode();
    } else {
      onFailedVpcCode();
    }
  };
